import { useMemo } from 'react'
import { FiGitBranch } from 'react-icons/fi'
import { RxLinkBreak2 } from 'react-icons/rx'
import { Button } from '~/@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/@/components/ui/tooltip'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

interface OverwriteLookupButtonProps {
  handleDetachClick: () => void
  handleCancelClick: () => void
  action: 'detach' | 'attach'
  isUnlinkedLookup: boolean
  className?: string
}

/**
 * The button to attach/detach a lookup cell
 */
export function OverwriteLookupButton({
  handleDetachClick,
  handleCancelClick,
  action,
  isUnlinkedLookup,
}: OverwriteLookupButtonProps) {
  const isDetach = action === 'detach'
  const { t } = useTypedTranslation()
  // If the cell was previously saved as overwriting the lookup, display 'Re-attach to lookup' if
  // action is attach. Otherwise, display 'Cancel'
  const buttonDisplay = useMemo(() => {
    if (isDetach) {
      return t('record.lookup.detach_lookup')
    }
    if (isUnlinkedLookup) {
      return t('record.lookup.reattach_lookup')
    }
    return t('common.actions.cancel')
  }, [isDetach, isUnlinkedLookup, t])

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="ms-2 w-8"
            onClick={isDetach ? handleDetachClick : handleCancelClick}
            size="icon"
            type="button"
            variant="ghost"
          >
            {isDetach ? <RxLinkBreak2 className="size-4" /> : <FiGitBranch className="size-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{buttonDisplay}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
